import * as React from "react";
import classnames from "classnames";
import { Text } from "@grandvisionhq/elements";
import { useIntl } from "@grandvisionhq/state";
import { FormContext } from "../../../../Form/Form/Form.js";
import Input from "../../../../Input/Input/Input.js";
import { Toggletip } from "../../../../Toggletip/Toggletip/Toggletip.js";
import { isDate, isValidDay, isValidMonth, isValidYear } from "../../../../Util/Util/validations.js";
import { initializeFields, isEqualState, validateFullDate, updateFields, validateWithRequired, joinValues, focusElementWhenValueReady, getFieldNames, getErrorMessageBasedOnValidation } from "./utils.js";
/* empty css          */
const Date = ({
  name,
  label,
  subHeading,
  validations,
  disabled = false,
  required = false,
  initialState,
  additionalScreenReaderLabel,
  tooltipText,
  hideAsteriskSign,
  fieldLabels,
  onChange,
  onBlur,
  className,
  invalidDateErrorMessage,
  ...rest
}) => {
  const { getLabel } = useIntl();
  const defaultValidations = React.useMemo(
    () => [
      {
        validator: isDate,
        errorMessage: invalidDateErrorMessage
      }
    ],
    [invalidDateErrorMessage]
  );
  const [validationResults, setValidationResults] = React.useState([]);
  const [isFullDateValid, setIsFullDateValid] = React.useState(true);
  const [state, setState] = React.useState({
    name,
    fields: {},
    isTouched: !!initialState?.isTouched,
    isValid: !!initialState?.isValid,
    value: initialState?.value ?? "",
    errorMessage: invalidDateErrorMessage
  });
  const formContext = React.useContext(FormContext);
  const monthRef = React.createRef();
  const yearRef = React.createRef();
  React.useEffect(() => {
    const fields = initializeFields({
      name,
      value: state.value,
      required
    });
    setState({ ...state, value: state.value, fields });
    formContext.onChange?.({ ...state, value: state.value });
    onChange?.({ ...state, value: state.value });
  }, []);
  React.useEffect(() => {
    const initialState2 = formContext.getInitialState?.(name);
    const fields = initializeFields({
      name,
      value: initialState2?.value,
      isTouched: initialState2?.isTouched,
      required
    });
    const newState = {
      ...state,
      fields,
      value: initialState2?.value ?? "",
      isTouched: !!initialState2?.isTouched,
      isValid: handleValidateFullDate(fields)
    };
    if (!isEqualState(state, newState)) {
      setState(newState);
      formContext.onChange?.(newState);
      onChange?.(newState);
    }
  }, [formContext]);
  const handleValidateFullDate = React.useCallback(
    (fields) => {
      const allValidations = [...validations || [], ...defaultValidations];
      if (!allValidations) {
        return true;
      }
      const validateFullDateResults = allValidations.map(
        ({ validator }) => validateFullDate(name, fields, required || false, validator)
      );
      const isValid = validateFullDateResults.indexOf(false) === -1;
      setValidationResults(validateFullDateResults);
      setIsFullDateValid(isValid);
      return isValid;
    },
    [defaultValidations, name, required, validations]
  );
  const handleInputChange = React.useCallback(
    ({ state: inputState, name: inputName, validator, ref }) => {
      const fields = updateFields(inputName, state.fields, {
        value: inputState.value,
        isValid: validateWithRequired(inputState.value, validator, { required })
      });
      const formState = {
        ...state,
        fields,
        value: joinValues(name, fields),
        isValid: handleValidateFullDate(fields),
        name
      };
      const fieldIsValid = fields[inputName]?.isValid ?? true;
      ref && fieldIsValid && focusElementWhenValueReady(inputState.value, ref);
      setState(formState);
      formContext.onChange?.(formState);
      onChange?.(formState);
    },
    [state, formContext, name, required, onChange, handleValidateFullDate]
  );
  const handleBlur = React.useCallback(
    (name2) => {
      const fields = updateFields(name2, state.fields, {
        value: state.fields?.[name2]?.value ?? "",
        isTouched: true
      });
      const isAllFieldsTouched = !Object.values(fields ?? {}).some(({ isTouched }) => !isTouched);
      const newState = {
        ...state,
        fields,
        isTouched: isAllFieldsTouched
      };
      setState(newState);
      formContext.onBlur?.(newState);
      onBlur?.(newState);
    },
    [onBlur, formContext, state]
  );
  const isAllFieldsValid = React.useMemo(
    () => !Object.values(state?.fields ?? {}).some(({ isValid }) => !isValid),
    [state]
  );
  const applyClasses = React.useCallback(
    (name2) => {
      const field = state.fields?.[name2];
      if (!field) {
        return "form-control__input";
      }
      if (isAllFieldsValid && !isFullDateValid && state.isTouched === true) {
        return "form-control__input _is_invalid";
      }
      return classnames("form-control__input", {
        _is_valid: field?.value && field?.isValid && field?.isTouched === true,
        _is_invalid: !field?.isValid && field?.isTouched === true
      });
    },
    [isAllFieldsValid, isFullDateValid, state]
  );
  const { day, month, year } = getFieldNames(name);
  const handleOnDayChange = React.useCallback(
    (state2) => {
      handleInputChange({ state: state2, name: day, validator: isValidDay, ref: monthRef });
    },
    [day, monthRef, handleInputChange]
  );
  const handleOnMonthChange = React.useCallback(
    (state2) => {
      handleInputChange({ state: state2, name: month, validator: isValidMonth, ref: yearRef });
    },
    [month, yearRef, handleInputChange]
  );
  const handleOnYearChange = React.useCallback(
    (state2) => {
      handleInputChange({ state: state2, name: year, validator: isValidYear });
    },
    [year, handleInputChange]
  );
  const handleOnDayBlur = React.useCallback(() => handleBlur(day), [day, handleBlur]);
  const handleOnMonthBlur = React.useCallback(() => handleBlur(month), [month, handleBlur]);
  const handleOnYearBlur = React.useCallback(() => handleBlur(year), [year, handleBlur]);
  const showAsterisk = required && !hideAsteriskSign;
  const showTopContainer = !!additionalScreenReaderLabel || showAsterisk || !!tooltipText;
  return /* @__PURE__ */ React.createElement(
    "fieldset",
    {
      ...rest,
      name,
      className: classnames("date-field", className),
      ...additionalScreenReaderLabel && { "aria-describedby": "additionalScreenReaderLabel" }
    },
    /* @__PURE__ */ React.createElement("div", { className: "date-field__title" }, label && /* @__PURE__ */ React.createElement("legend", { className: "date-field__legend" }, label), showTopContainer && /* @__PURE__ */ React.createElement("div", { className: "date-field__top-container" }, additionalScreenReaderLabel && /* @__PURE__ */ React.createElement(
      "span",
      {
        className: "date-field__additional-screen-reader-label",
        id: "additionalScreenReaderLabel"
      },
      additionalScreenReaderLabel
    ), showAsterisk && /* @__PURE__ */ React.createElement("span", { className: "date-field__asterisk" }, "*"), tooltipText && /* @__PURE__ */ React.createElement(Toggletip, { message: tooltipText, className: "date-field__tooltip" }))),
    subHeading && /* @__PURE__ */ React.createElement(Text, { color: "text-secondary", className: "date-field__subheading" }, subHeading),
    /* @__PURE__ */ React.createElement("div", { className: "date-field__container" }, /* @__PURE__ */ React.createElement("label", { className: "date-field__label date-field__label__day", htmlFor: day }, fieldLabels?.[day] && fieldLabels[day], /* @__PURE__ */ React.createElement(
      Input,
      {
        id: day,
        placeholder: getLabel("common.placeholder.day.abbr"),
        className: applyClasses(day),
        disabled,
        onChange: handleOnDayChange,
        onBlur: handleOnDayBlur,
        initialValue: state?.fields?.[day]?.value ?? "",
        pattern: "^\\d{0,2}$",
        inputMode: "numeric"
      }
    )), /* @__PURE__ */ React.createElement("label", { className: "date-field__label date-field__label__month", htmlFor: month }, fieldLabels?.[month] && fieldLabels[month], /* @__PURE__ */ React.createElement(
      Input,
      {
        id: month,
        placeholder: getLabel("common.placeholder.month.abbr"),
        className: applyClasses(month),
        disabled,
        inputRef: monthRef,
        onChange: handleOnMonthChange,
        onBlur: handleOnMonthBlur,
        initialValue: state?.fields?.[month]?.value ?? "",
        pattern: "^\\d{0,2}$",
        inputMode: "numeric"
      }
    )), /* @__PURE__ */ React.createElement("label", { className: "date-field__label date-field__label__year", htmlFor: year }, fieldLabels?.[year] && fieldLabels[year], /* @__PURE__ */ React.createElement(
      Input,
      {
        id: year,
        placeholder: getLabel("common.placeholder.year.abbr"),
        className: applyClasses(year),
        disabled,
        inputRef: yearRef,
        onChange: handleOnYearChange,
        onBlur: handleOnYearBlur,
        initialValue: state?.fields?.[year]?.value ?? "",
        pattern: "^\\d{0,4}$",
        inputMode: "numeric"
      }
    ))),
    state.isTouched && !state.isValid && /* @__PURE__ */ React.createElement("span", { className: "date-field__error" }, getErrorMessageBasedOnValidation(
      validationResults,
      [...validations || [], ...defaultValidations].map(({ errorMessage }) => errorMessage)
    ))
  );
};
export {
  Date as default
};
//# sourceMappingURL=Date.js.map
